import { getErrorMessage } from "@/lib/error";
import { toastError } from "@/lib/toast-error";
import { useGetMe, useGetMailbox, useDisconnectMailbox, useGetSalesforceStatus, useGetSalesforceConfig, useCheckSalesforceFieldMapping, useTestSalesforceConfig, getGetSalesforceConfigQueryKey, getGetSalesforceStatusQueryKey, getCheckSalesforceFieldMappingQueryKey, useGetAdminSettings, useUpdateAdminSettings, getGetAdminSettingsQueryKey, useGetMailboxOauthStatus, getGetMailboxOauthStatusQueryKey, useGetMailboxOauthRedirects, getGetMailboxOauthRedirectsQueryKey, useGetCopyOnRegPref, useUpdateCopyOnRegPref, getGetCopyOnRegPrefQueryKey, useGetSupportEmailPref, useUpdateSupportEmailPref, getGetSupportEmailPrefQueryKey, useListCatalogProducts, useCreateCatalogProduct, useUpdateCatalogProduct, useDeleteCatalogProduct, getListCatalogProductsQueryKey, useBulkAddCatalogProducts, usePreviewBulkAddCatalogProducts, useResetDemoData, useGetEmailProviderConfig, useUpdateEmailProviderConfig, useClearEmailProviderConfig, useTestEmailProviderSend, getGetEmailProviderConfigQueryKey, useGetSystemHealth, useRefreshSystemHealth, getGetSystemHealthQueryKey, useGetMailboxPollHealth, getGetMailboxPollHealthQueryKey, useRunScheduledReportNow, useGetDbHealth, getGetDbHealthQueryKey } from "@workspace/api-client-react";
import type { SystemHealthItem, MailboxPollHealth } from "@workspace/api-client-react";
import type { PreviewBulkAddCatalogProductsResult } from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Mail, ShieldCheck, Download, AlertCircle, Building2, Loader2, LogOut, LifeBuoy, Plug, Pencil, ChevronDown, CheckCircle2, XCircle, Copy, Package, Plus, Trash2, Check, List, RotateCcw, AlertTriangle, Send, ServerCog, Database, RefreshCw, Clock, HardDrive, Activity, ExternalLink, Flame, KeyRound, Users } from "lucide-react";
import { toast } from "sonner";
import { useEffect, useRef, useState } from "react";
import { formatDateTime, formatRelativeNextRun } from "@/lib/format";
import { useQueryClient } from "@tanstack/react-query";
import { ConnectSalesforceDialog, DisconnectSalesforceButton } from "@/components/ConnectSalesforceDialog";
import { McpServersSection } from "@/components/McpServersSection";
import { AdminUsersSection } from "@/components/AdminUsersSection";
import { BulkAddProductsDialog } from "@/components/BulkAddProductsDialog";
import { MailboxOAuthStatusBadges } from "@/components/MailboxOAuthStatusBadges";

function HealthStatusIndicator({ status }: { status: string }) {
  if (status === "ok") {
    return <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 shrink-0" />;
  }
  if (status === "warning") {
    return <AlertTriangle className="h-4 w-4 text-amber-500 dark:text-amber-400 shrink-0" />;
  }
  if (status === "error") {
    return <XCircle className="h-4 w-4 text-destructive shrink-0" />;
  }
  return <AlertCircle className="h-4 w-4 text-muted-foreground shrink-0" />;
}

async function startMailboxReconnect(provider: "google" | "microsoft"): Promise<void> {
  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
  const label = provider === "google" ? "Google" : "Microsoft 365";
  try {
    const res = await fetch(`${BASE}/api/mailbox/oauth/start?provider=${provider}`, {
      credentials: "include",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      toast.error(data.error ?? `Failed to start ${label} reconnect flow`);
      return;
    }
    if (!data.authorizeUrl) {
      toast.error("No authorization URL returned. Contact an admin.");
      return;
    }
    const popup = window.open(
      data.authorizeUrl,
      "oauth-popup",
      "width=600,height=700,popup=1",
    );
    if (!popup) {
      toast.error("Popup was blocked. Please allow popups for this site and try again.");
    }
  } catch {
    toast.error(`Failed to start ${label} reconnect flow`);
  }
}

function detectMailboxProviderFromLabel(label: string): "google" | "microsoft" | null {
  if (/^Gmail\b/i.test(label)) return "google";
  if (/^Microsoft\b/i.test(label)) return "microsoft";
  return null;
}

function isCredentialExpiryItem(id: string): boolean {
  return (
    id === "encryption-key-age" ||
    id === "salesforce-credential-age" ||
    id.startsWith("mailbox-token-age:")
  );
}

function parseDaysRemaining(message: string | undefined): number | null {
  if (!message) return null;
  const remaining = message.match(/(\d+)\s+days?\s+remaining/i);
  if (remaining) return parseInt(remaining[1], 10);
  const inDays = message.match(/in\s+(\d+)\s+days?/i);
  if (inDays) return parseInt(inDays[1], 10);
  return null;
}

function ExpiryBadge({ status, days }: { status: string; days: number | null }) {
  const expired = status === "error";
  const colorClass = expired
    ? "bg-red-100 text-red-800 border-red-200 dark:bg-red-950 dark:text-red-200 dark:border-red-900"
    : status === "warning"
      ? "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:border-amber-900"
      : "bg-green-100 text-green-800 border-green-200 dark:bg-green-950 dark:text-green-200 dark:border-green-900";
  const label = expired
    ? "Expired"
    : days != null
      ? `${days} day${days === 1 ? "" : "s"} remaining`
      : null;
  if (!label) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium ${colorClass}`}
    >
      <Clock className="h-3 w-3" />
      {label}
    </span>
  );
}

export function HealthItemCard({ item }: { item: SystemHealthItem }) {
  const statusColors: Record<string, string> = {
    ok: "border-green-200 dark:border-green-900",
    warning: "border-amber-200 dark:border-amber-900",
    error: "border-red-200 dark:border-red-900",
    unknown: "border-border",
  };
  const borderClass = statusColors[item.status] ?? "border-border";

  const isExpiryItem = isCredentialExpiryItem(item.id);
  const days = isExpiryItem ? parseDaysRemaining(item.message) : null;
  const showBadge = isExpiryItem && (days != null || item.status === "error");

  const isMailboxToken = item.id.startsWith("mailbox-token-age:");
  const isEncryptionKey = item.id === "encryption-key-age";
  const mailboxProvider = isMailboxToken
    ? detectMailboxProviderFromLabel(item.label)
    : null;

  const actionLabel = item.actionLabel ?? null;
  const actionHref = item.actionHref ?? null;

  return (
    <div className={`flex items-start justify-between gap-3 p-3 rounded-lg border ${borderClass} bg-card`}>
      <div className="flex items-start gap-2.5 min-w-0">
        <HealthStatusIndicator status={item.status} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm">{item.label}</span>
            {showBadge && <ExpiryBadge status={item.status} days={days} />}
          </div>
          {item.message && (
            <div className="text-xs text-muted-foreground mt-0.5 break-words">{item.message}</div>
          )}
          {isEncryptionKey && (
            <div className="text-xs text-muted-foreground mt-1 break-words">
              After rotating the encryption key, set the{" "}
              <code className="px-1 py-0.5 rounded bg-muted text-foreground text-[11px]">
                ENCRYPTION_KEY_CREATED_AT
              </code>{" "}
              environment variable to today's date (ISO format) so age tracking restarts.
            </div>
          )}
          <div className="text-xs text-muted-foreground mt-0.5">
            Checked {formatDateTime(item.lastCheckedAt)}
          </div>
        </div>
      </div>
      {isMailboxToken && mailboxProvider ? (
        <button
          type="button"
          onClick={() => {
            void startMailboxReconnect(mailboxProvider);
          }}
          className="shrink-0 inline-flex items-center gap-1 text-xs text-primary hover:underline whitespace-nowrap mt-0.5"
        >
          Renew now
          <ExternalLink className="h-3 w-3" />
        </button>
      ) : actionLabel && actionHref ? (
        <a
          href={actionHref}
          className="shrink-0 inline-flex items-center gap-1 text-xs text-primary hover:underline whitespace-nowrap mt-0.5"
        >
          {actionLabel}
          <ExternalLink className="h-3 w-3" />
        </a>
      ) : null}
    </div>
  );
}

export function SystemHealthCard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const { data: healthReport, isLoading, isFetching } = useGetSystemHealth({
    query: {
      enabled: isAdmin,
      queryKey: getGetSystemHealthQueryKey(),
      refetchInterval: 60_000,
    },
  });
  const refreshHealth = useRefreshSystemHealth();

  const handleRefreshAll = () => {
    refreshHealth.mutate(undefined, {
      onSuccess: (data) => {
        queryClient.setQueryData(getGetSystemHealthQueryKey(), data);
      },
      onError: (err: unknown) => {
        toastError(err, "Failed to refresh system health");
      },
    });
  };

  const overallStatus = healthReport?.items.some((i) => i.status === "error")
    ? "error"
    : healthReport?.items.some((i) => i.status === "warning")
      ? "warning"
      : healthReport?.items.every((i) => i.status === "ok")
        ? "ok"
        : "unknown";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            System Health
            {healthReport && !isLoading && (
              <span className="ml-1">
                <HealthStatusIndicator status={overallStatus} />
              </span>
            )}
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            disabled={refreshHealth.isPending || isFetching}
          >
            {refreshHealth.isPending || isFetching ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
            )}
            Refresh all
          </Button>
        </div>
        <CardDescription>
          Live status of every critical integration. Auto-refreshes every minute.
          {healthReport?.cachedUntil && (
            <span className="ml-1 text-xs">
              (Results cached until {formatDateTime(healthReport.cachedUntil)})
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        ) : healthReport?.items && healthReport.items.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {healthReport.items.map((item) => (
              <HealthItemCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No health data available.</p>
        )}
      </CardContent>
    </Card>
  );
}

function DatabaseHealthCard({ isAdmin }: { isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const { data, isLoading, isFetching, refetch } = useGetDbHealth({
    query: {
      enabled: isAdmin,
      queryKey: getGetDbHealthQueryKey(),
      refetchInterval: 60_000,
    },
  });

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: getGetDbHealthQueryKey() });
    refetch();
  };

  return (
    <Card id="db-health">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Database Health
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isFetching}
          >
            {isFetching ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-3.5 w-3.5" />
            )}
            Refresh
          </Button>
        </div>
        <CardDescription>
          Connection pool stats and slow query tracking. Auto-refreshes every minute.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
        ) : data ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <HardDrive className="h-4 w-4 text-muted-foreground" />
                Connection Pool
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-2xl font-bold">{data.pool.total}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Total</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">{data.pool.idle}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Idle</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${data.pool.waiting > 0 ? "text-amber-500" : ""}`}>
                    {data.pool.waiting}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Waiting</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Captured {formatDateTime(data.pool.capturedAt)}
              </div>
            </div>
            <div className="rounded-lg border bg-card p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Flame className="h-4 w-4 text-muted-foreground" />
                Slow Queries (last hour)
              </div>
              <div className="grid grid-cols-2 gap-2 text-center">
                <div>
                  <div className={`text-2xl font-bold ${data.slowQueries.count > 0 ? "text-amber-500 dark:text-amber-400" : ""}`}>
                    {data.slowQueries.count}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">Count</div>
                </div>
                <div>
                  <div className={`text-2xl font-bold ${data.slowQueries.p95Ms != null && data.slowQueries.p95Ms >= data.thresholds.errorMs ? "text-destructive" : data.slowQueries.p95Ms != null && data.slowQueries.p95Ms >= data.thresholds.warnMs ? "text-amber-500" : ""}`}>
                    {data.slowQueries.p95Ms != null ? `${data.slowQueries.p95Ms}ms` : "—"}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">p95</div>
                </div>
              </div>
              <div className="text-xs text-muted-foreground">
                Thresholds: warn ≥{data.thresholds.warnMs}ms · error ≥{data.thresholds.errorMs}ms
              </div>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No database health data available.</p>
        )}
      </CardContent>
    </Card>
  );
}

export default function Settings() {
  const { data: me, isLoading: isLoadingMe } = useGetMe();
  const { data: mailbox, isLoading: isLoadingMailbox, refetch: refetchMailbox } = useGetMailbox();
  const { data: sfStatus, isLoading: isLoadingSf } = useGetSalesforceStatus();
  
  const isAdmin = !!me?.isAdmin;
  const { data: sfConfig, isLoading: isLoadingSfConfig } = useGetSalesforceConfig({
    query: { queryKey: getGetSalesforceConfigQueryKey() },
  });

  const { data: oauthStatus } = useGetMailboxOauthStatus({
    query: { enabled: isAdmin, queryKey: getGetMailboxOauthStatusQueryKey() },
  });

  const { data: pollHealth } = useGetMailboxPollHealth({
    query: { enabled: isAdmin, queryKey: getGetMailboxPollHealthQueryKey(), refetchInterval: 60_000 },
  });

  const disconnectMailbox = useDisconnectMailbox();
  const queryClient = useQueryClient();

  const [isExporting, setIsExporting] = useState(false);
  const [showConnectDialog, setShowConnectDialog] = useState(false);
  const [oauthPending, setOauthPending] = useState<"google" | "microsoft" | null>(null);

  const { data: adminSettings, isLoading: isLoadingAdminSettings } = useGetAdminSettings({
    query: { enabled: isAdmin, queryKey: getGetAdminSettingsQueryKey() },
  });
  const { data: oauthRedirects } = useGetMailboxOauthRedirects({
    query: {
      enabled: isAdmin && !mailbox?.connected,
      queryKey: getGetMailboxOauthRedirectsQueryKey(),
    },
  });

  const handleCopyRedirect = async (uri: string, label: string) => {
    try {
      await navigator.clipboard.writeText(uri);
      toast.success(`${label} redirect URI copied`);
    } catch {
      toast.error("Failed to copy. Select and copy manually.");
    }
  };
  const updateAdminSettings = useUpdateAdminSettings();
  const [supportEmailInput, setSupportEmailInput] = useState("");
  const resetDemoData = useResetDemoData();
  const [showResetDemoDialog, setShowResetDemoDialog] = useState(false);

  const handleResetDemoData = () => {
    resetDemoData.mutate(undefined, {
      onSuccess: (result) => {
        const c = result.cleared;
        const total = c.deals + c.supplierContacts + c.suppliers + c.supportTickets;
        toast.success(
          total > 0
            ? `Demo data reset: removed ${c.deals} deals, ${c.suppliers} suppliers, ${c.supplierContacts} supplier contacts, ${c.supportTickets} support tickets, then re-seeded.`
            : "Demo data re-seeded (nothing to clear).",
        );
        setShowResetDemoDialog(false);
        // Refresh data the user is likely looking at after a wipe.
        queryClient.invalidateQueries();
      },
      onError: (err: unknown) => {
        toastError(err, "Failed to reset demo data");
      },
    });
  };

  useEffect(() => {
    if (adminSettings) {
      setSupportEmailInput(adminSettings.supportEmail ?? "");
    }
  }, [adminSettings]);

  const [backupIntervalInput, setBackupIntervalInput] = useState<string>("");
  const [staleYellowInput, setStaleYellowInput] = useState<string>("");
  const [staleRedInput, setStaleRedInput] = useState<string>("");

  useEffect(() => {
    if (adminSettings) {
      setStaleYellowInput(String(adminSettings.staleYellowDays));
      setStaleRedInput(String(adminSettings.staleRedDays));
    }
  }, [adminSettings]);

  useEffect(() => {
    if (adminSettings) {
      setBackupIntervalInput(String(adminSettings.backupVerifyIntervalHours));
    }
  }, [adminSettings]);

  const handleSaveBackupInterval = () => {
    const trimmed = backupIntervalInput.trim();
    const min = adminSettings?.backupVerifyIntervalHoursMin ?? 1;
    const max = adminSettings?.backupVerifyIntervalHoursMax ?? 168;
    const parsed = parseInt(trimmed, 10);
    if (isNaN(parsed) || parsed < min || parsed > max) {
      toast.error(`Enter a whole number of hours between ${min} and ${max}.`);
      return;
    }
    updateAdminSettings.mutate(
      { data: { backupVerifyIntervalHours: parsed } },
      {
        onSuccess: () => {
          toast.success(
            `Backup verification interval saved (every ${parsed} hour${parsed === 1 ? "" : "s"}). Takes effect on the next server restart.`,
          );
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to save backup verification interval");
        },
      },
    );
  };

  const handleResetBackupInterval = () => {
    updateAdminSettings.mutate(
      { data: { backupVerifyIntervalHours: null } },
      {
        onSuccess: () => {
          const def = adminSettings?.backupVerifyIntervalHoursDefault ?? 24;
          toast.success(
            `Backup verification interval reset to default (every ${def} hours). Takes effect on the next server restart.`,
          );
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to reset backup verification interval");
        },
      },
    );
  };

  const handleSaveStaleThresholds = () => {
    const yellow = parseInt(staleYellowInput.trim(), 10);
    const red = parseInt(staleRedInput.trim(), 10);
    const yellowMin = adminSettings?.staleYellowDaysMin ?? 1;
    const yellowMax = adminSettings?.staleYellowDaysMax ?? 365;
    const redMin = adminSettings?.staleRedDaysMin ?? 1;
    const redMax = adminSettings?.staleRedDaysMax ?? 365;
    if (isNaN(yellow) || yellow < yellowMin || yellow > yellowMax) {
      toast.error(`Yellow threshold must be between ${yellowMin} and ${yellowMax} days`);
      return;
    }
    if (isNaN(red) || red < redMin || red > redMax) {
      toast.error(`Red threshold must be between ${redMin} and ${redMax} days`);
      return;
    }
    if (yellow >= red) {
      toast.error("Yellow threshold must be less than the red threshold");
      return;
    }
    updateAdminSettings.mutate(
      { data: { staleYellowDays: yellow, staleRedDays: red } },
      {
        onSuccess: () => {
          toast.success("Stale deal thresholds saved.");
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to save stale thresholds");
        },
      },
    );
  };

  const [credentialWarnInput, setCredentialWarnInput] = useState<string>("");
  const [encryptionKeyMaxAgeInput, setEncryptionKeyMaxAgeInput] = useState<string>("");
  const [mailboxTokenMaxAgeInput, setMailboxTokenMaxAgeInput] = useState<string>("");
  const [sfCredentialMaxAgeInput, setSfCredentialMaxAgeInput] = useState<string>("");

  useEffect(() => {
    if (adminSettings) {
      setCredentialWarnInput(String(adminSettings.credentialWarnDays));
      setEncryptionKeyMaxAgeInput(String(adminSettings.encryptionKeyMaxAgeDays));
      setMailboxTokenMaxAgeInput(String(adminSettings.mailboxTokenMaxAgeDays));
      setSfCredentialMaxAgeInput(String(adminSettings.sfCredentialMaxAgeDays));
    }
  }, [adminSettings]);

  const handleSaveCredentialThresholds = () => {
    const warnMin = adminSettings?.credentialWarnDaysMin ?? 1;
    const warnMax = adminSettings?.credentialWarnDaysMax ?? 365;
    const ageMin = adminSettings?.credentialMaxAgeDaysMin ?? 1;
    const ageMax = adminSettings?.credentialMaxAgeDaysMax ?? 3650;

    const warn = parseInt(credentialWarnInput.trim(), 10);
    const enc = parseInt(encryptionKeyMaxAgeInput.trim(), 10);
    const mbox = parseInt(mailboxTokenMaxAgeInput.trim(), 10);
    const sf = parseInt(sfCredentialMaxAgeInput.trim(), 10);

    if (isNaN(warn) || warn < warnMin || warn > warnMax) {
      toast.error(`Warn-ahead days must be between ${warnMin} and ${warnMax}.`);
      return;
    }
    for (const [label, value] of [
      ["Encryption key max age", enc],
      ["Mailbox token max age", mbox],
      ["Salesforce credential max age", sf],
    ] as const) {
      if (isNaN(value) || value < ageMin || value > ageMax) {
        toast.error(`${label} must be between ${ageMin} and ${ageMax} days.`);
        return;
      }
      if (value <= warn) {
        toast.error(`${label} must be greater than the warn-ahead window (${warn} days).`);
        return;
      }
    }

    updateAdminSettings.mutate(
      {
        data: {
          credentialWarnDays: warn,
          encryptionKeyMaxAgeDays: enc,
          mailboxTokenMaxAgeDays: mbox,
          sfCredentialMaxAgeDays: sf,
        },
      },
      {
        onSuccess: () => {
          toast.success("Credential expiry thresholds saved.");
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() });
        },
        onError: (err: unknown) => {
          toast.error(getErrorMessage(err, "Failed to save credential thresholds"));
        },
      },
    );
  };

  const handleResetCredentialThresholds = () => {
    updateAdminSettings.mutate(
      {
        data: {
          credentialWarnDays: null,
          encryptionKeyMaxAgeDays: null,
          mailboxTokenMaxAgeDays: null,
          sfCredentialMaxAgeDays: null,
        },
      },
      {
        onSuccess: () => {
          toast.success("Credential expiry thresholds reset to defaults.");
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() });
        },
        onError: (err: unknown) => {
          toast.error(getErrorMessage(err, "Failed to reset credential thresholds"));
        },
      },
    );
  };

  const handleResetStaleThresholds = () => {
    updateAdminSettings.mutate(
      { data: { staleYellowDays: null, staleRedDays: null } },
      {
        onSuccess: () => {
          const yDef = adminSettings?.staleYellowDaysDefault ?? 5;
          const rDef = adminSettings?.staleRedDaysDefault ?? 10;
          toast.success(`Stale thresholds reset to defaults (yellow ${yDef}d / red ${rDef}d).`);
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to reset stale thresholds");
        },
      },
    );
  };

  const [reportsFrequency, setReportsFrequency] = useState<"off" | "weekly" | "monthly">("off");
  const [reportsDayOfWeek, setReportsDayOfWeek] = useState<number>(1);
  const [reportsDayOfMonth, setReportsDayOfMonth] = useState<number>(1);
  const [reportsHourUtc, setReportsHourUtc] = useState<number>(13);
  const [reportsRecipientsText, setReportsRecipientsText] = useState<string>("");
  const [reportsIncludeAdmins, setReportsIncludeAdmins] = useState<boolean>(true);

  useEffect(() => {
    if (adminSettings?.reportsSchedule) {
      const s = adminSettings.reportsSchedule;
      setReportsFrequency(s.frequency);
      setReportsDayOfWeek(s.dayOfWeek);
      setReportsDayOfMonth(s.dayOfMonth);
      setReportsHourUtc(s.hourUtc);
      setReportsRecipientsText(s.recipients.join(", "));
      setReportsIncludeAdmins(s.includeAdmins);
    }
  }, [adminSettings]);

  const runReportNow = useRunScheduledReportNow();

  const parseRecipients = (text: string): string[] => {
    return text
      .split(/[,\n;\s]+/)
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0);
  };

  const handleSaveReportsSchedule = () => {
    const recipients = parseRecipients(reportsRecipientsText);
    const max = adminSettings?.reportsScheduleMaxRecipients ?? 50;
    if (recipients.length > max) {
      toast.error(`At most ${max} recipients are allowed.`);
      return;
    }
    if (reportsFrequency !== "off" && recipients.length === 0 && !reportsIncludeAdmins) {
      toast.error("Add at least one recipient or enable 'Include admins'.");
      return;
    }
    updateAdminSettings.mutate(
      {
        data: {
          reportsSchedule: {
            frequency: reportsFrequency,
            dayOfWeek: reportsDayOfWeek,
            dayOfMonth: reportsDayOfMonth,
            hourUtc: reportsHourUtc,
            recipients,
            includeAdmins: reportsIncludeAdmins,
          },
        },
      },
      {
        onSuccess: () => {
          toast.success(
            reportsFrequency === "off"
              ? "Scheduled reports disabled."
              : `Scheduled reports saved (${reportsFrequency}).`,
          );
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to save scheduled reports");
        },
      },
    );
  };

  const handleRunReportNow = () => {
    const freq = reportsFrequency === "monthly" ? "monthly" : "weekly";
    runReportNow.mutate(
      { data: { frequency: freq } },
      {
        onSuccess: (res) => {
          if (res.ok) {
            toast.success(`Sent report to ${res.recipientsCount} recipient${res.recipientsCount === 1 ? "" : "s"}.`);
          } else {
            toast.error(res.error ?? "Failed to send report");
          }
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to send report");
        },
      },
    );
  };

  const { data: copyPref, isLoading: isLoadingCopyPref } = useGetCopyOnRegPref({
    query: { queryKey: getGetCopyOnRegPrefQueryKey() },
  });
  const updateCopyPref = useUpdateCopyOnRegPref();
  const [copyEnabled, setCopyEnabled] = useState(false);
  const [copyMode, setCopyMode] = useState<"cc" | "bcc">("bcc");
  const [copyScope, setCopyScope] = useState<"initial" | "all">("initial");

  useEffect(() => {
    if (copyPref) {
      setCopyEnabled(copyPref.enabled ?? false);
      setCopyMode((copyPref.mode as "cc" | "bcc") ?? "bcc");
      setCopyScope((copyPref.scope as "initial" | "all") ?? "initial");
    }
  }, [copyPref]);

  const { data: supportEmailPref, isLoading: isLoadingSupportEmailPref } = useGetSupportEmailPref({
    query: { queryKey: getGetSupportEmailPrefQueryKey() },
  });
  const updateSupportEmailPref = useUpdateSupportEmailPref();
  const [supportEmailEnabled, setSupportEmailEnabled] = useState(true);

  useEffect(() => {
    if (supportEmailPref) {
      setSupportEmailEnabled(supportEmailPref.enabled ?? true);
    }
  }, [supportEmailPref]);

  const handleSaveSupportEmailPref = (next: boolean) => {
    setSupportEmailEnabled(next);
    updateSupportEmailPref.mutate(
      { data: { enabled: next } },
      {
        onSuccess: () => {
          toast.success(
            next
              ? "Support ticket email notifications enabled."
              : "Support ticket email notifications disabled.",
          );
          queryClient.invalidateQueries({ queryKey: getGetSupportEmailPrefQueryKey() });
        },
        onError: (err: unknown) => {
          // revert on failure
          setSupportEmailEnabled(!next);
          toastError(err, "Failed to save support email preference");
        },
      },
    );
  };

  const handleSaveCopyPref = () => {
    updateCopyPref.mutate(
      {
        data: {
          enabled: copyEnabled,
          mode: copyEnabled ? copyMode : undefined,
          scope: copyEnabled ? copyScope : undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success("Copy preference saved.");
          queryClient.invalidateQueries({ queryKey: getGetCopyOnRegPrefQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to save copy preference");
        },
      },
    );
  };

  const handleSaveSupportEmail = () => {
    const trimmed = supportEmailInput.trim();
    updateAdminSettings.mutate(
      { data: { supportEmail: trimmed === "" ? null : trimmed } },
      {
        onSuccess: () => {
          toast.success(
            trimmed === ""
              ? "Support email cleared. Falling back to environment variable."
              : "Support email updated.",
          );
          queryClient.invalidateQueries({ queryKey: getGetAdminSettingsQueryKey() });
        },
        onError: (err: unknown) => {
          toastError(err, "Failed to update support email");
        },
      },
    );
  };

  const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

  const startOAuth = async (provider: "google" | "microsoft") => {
    if (oauthPending) return;
    const label = provider === "google" ? "Google" : "Microsoft 365";
    setOauthPending(provider);
    try {
      const res = await fetch(`${BASE}/api/mailbox/oauth/start?provider=${provider}`, {
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error ?? `Failed to start ${label} OAuth`);
        setOauthPending(null);
        return;
      }
      if (!data.authorizeUrl) {
        toast.error("No authorization URL returned. Contact an admin.");
        setOauthPending(null);
        return;
      }
      const popup = window.open(
        data.authorizeUrl,
        "oauth-popup",
        "width=600,height=700,popup=1",
      );
      if (!popup) {
        toast.error("Popup was blocked. Please allow popups for this site and try again.");
        setOauthPending(null);
        return;
      }
      const timer = setInterval(() => {
        if (popup.closed) {
          clearInterval(timer);
          setOauthPending(null);
          refetchMailbox();
        }
      }, 500);
    } catch {
      toast.error(`Failed to start ${label} OAuth flow`);
      setOauthPending(null);
    }
  };

  const handleConnectGoogle = () => startOAuth("google");
  const handleConnectMicrosoft = () => startOAuth("microsoft");

  const handleDisconnect = () => {
    disconnectMailbox.mutate(undefined, {
      onSuccess: () => {
        toast.success("Mailbox disconnected successfully");
        refetchMailbox();
        queryClient.invalidateQueries({ queryKey: ["/api/me"] });
      },
      onError: (err: unknown) => {
        toastError(err, "Failed to disconnect mailbox");
      }
    });
  };

  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      const res = await fetch('/api/admin/registrations/export');
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tdm-registrations-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Export downloaded successfully");
    } catch (error) {
      toast.error("Failed to export registrations");
    } finally {
      setIsExporting(false);
    }
  };

  const sfConnected = sfStatus?.connected;
  const sfIsEnvConfig = sfConfig?.source === "env";

  const { data: sfFieldCheck, isLoading: isLoadingFieldCheck, refetch: refetchFieldCheck } = useCheckSalesforceFieldMapping({
    query: { enabled: isAdmin && !!sfConnected, queryKey: getCheckSalesforceFieldMappingQueryKey() },
  });
  const [fieldCheckOpen, setFieldCheckOpen] = useState(false);

  const testSalesforceMutation = useTestSalesforceConfig();
  const [sfTestResult, setSfTestResult] = useState<
    | { success: true; instanceUrl?: string; username?: string }
    | { success: false; error: string }
    | null
  >(null);

  const handleTestSalesforceConnection = () => {
    setSfTestResult(null);
    testSalesforceMutation.mutate(
      { data: {} },
      {
        onSuccess: (result) => {
          if (result.success) {
            setSfTestResult({
              success: true,
              instanceUrl: result.instanceUrl,
              username: result.username ?? sfConfig?.username,
            });
            queryClient.invalidateQueries({ queryKey: getGetSalesforceStatusQueryKey() });
            refetchFieldCheck();
          } else {
            setSfTestResult({
              success: false,
              error: result.error ?? "Salesforce connection test failed.",
            });
          }
        },
        onError: (err: unknown) => {
          setSfTestResult({
            success: false,
            error: getErrorMessage(err, "Salesforce connection test failed"),
          });
        },
      },
    );
  };

  // Reset the inline test result when the user re-opens the edit dialog or
  // disconnects/reconnects, to avoid showing stale messages.
  useEffect(() => {
    if (showConnectDialog) setSfTestResult(null);
  }, [showConnectDialog]);
  useEffect(() => {
    if (!sfConnected) setSfTestResult(null);
  }, [sfConnected]);

  const { data: catalogProducts, isLoading: isLoadingCatalog } = useListCatalogProducts({
    query: { enabled: isAdmin, queryKey: getListCatalogProductsQueryKey() },
  });
  const createProduct = useCreateCatalogProduct();
  const updateProduct = useUpdateCatalogProduct();
  const deleteProduct = useDeleteCatalogProduct();

  const [newProductName, setNewProductName] = useState("");
  const [editingProductId, setEditingProductId] = useState<string | null>(null);
  const [editingProductName, setEditingProductName] = useState("");
  const editInputRef = useRef<HTMLInputElement>(null);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);

  useEffect(() => {
    if (editingProductId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingProductId]);

  type JobStats = { queued: number; running: number; done: number; failed: number; cancelled: number };
  type JobSummary = { id: string; kind: string; status: string; attempts: number; maxAttempts: number; lastError: string | null; payload: unknown; createdAt: string; runAt: string; completedAt: string | null };

  const [jobStats, setJobStats] = useState<JobStats | null>(null);
  const [jobs, setJobs] = useState<JobSummary[]>([]);
  const [jobsStatus, setJobsStatus] = useState<string>("failed");
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobActionPending, setJobActionPending] = useState<string | null>(null);

  const loadJobData = async () => {
    if (!isAdmin) return;
    setJobsLoading(true);
    try {
      const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
      const [statsRes, listRes] = await Promise.all([
        fetch(`${BASE}/api/admin/jobs/stats`, { credentials: "include" }),
        fetch(`${BASE}/api/admin/jobs?status=${jobsStatus}&limit=50`, { credentials: "include" }),
      ]);
      if (statsRes.ok) setJobStats(await statsRes.json());
      if (listRes.ok) {
        const data = await listRes.json();
        setJobs(data.jobs ?? []);
      }
    } catch {
      // silent
    } finally {
      setJobsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadJobData();
  }, [isAdmin, jobsStatus]);

  const handleJobRetry = async (id: string) => {
    setJobActionPending(id);
    try {
      const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
      await fetch(`${BASE}/api/admin/jobs/${id}/retry`, { method: "POST", credentials: "include" });
      toast.success("Job re-queued");
      await loadJobData();
    } catch {
      toast.error("Failed to retry job");
    } finally {
      setJobActionPending(null);
    }
  };

  const handleJobCancel = async (id: string) => {
    setJobActionPending(id);
    try {
      const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
      await fetch(`${BASE}/api/admin/jobs/${id}/cancel`, { method: "POST", credentials: "include" });
      toast.success("Job cancelled");
      await loadJobData();
    } catch {
      toast.error("Failed to cancel job");
    } finally {
      setJobActionPending(null);
    }
  };

  type BackupRun = {
    id: string;
    startedAt: string;
    completedAt: string | null;
    status: string;
    durationMs: number | null;
    dumpSizeBytes: number | null;
    tableCount: number | null;
    errorMessage: string | null;
    triggeredBy: string;
  };

  const [backupLatest, setBackupLatest] = useState<BackupRun | null | undefined>(undefined);
  const [backupHistory, setBackupHistory] = useState<BackupRun[]>([]);
  const [backupNextRunAt, setBackupNextRunAt] = useState<string | null>(null);
  const [backupRunning, setBackupRunning] = useState(false);
  const [backupError, setBackupError] = useState<string | null>(null);
  const [backupHistoryOpen, setBackupHistoryOpen] = useState(false);

  const loadBackupData = async () => {
    if (!isAdmin) return;
    try {
      const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
      const [latestRes, historyRes, nextRes] = await Promise.all([
        fetch(`${BASE}/api/admin/backup-verification/latest`, { credentials: "include" }),
        fetch(`${BASE}/api/admin/backup-verification/runs`, { credentials: "include" }),
        fetch(`${BASE}/api/admin/backup-verification/next-run`, { credentials: "include" }),
      ]);
      if (latestRes.ok) setBackupLatest(await latestRes.json());
      else setBackupLatest(null);
      if (historyRes.ok) setBackupHistory(await historyRes.json());
      if (nextRes.ok) {
        const data = (await nextRes.json()) as { nextRunAt: string | null };
        setBackupNextRunAt(data.nextRunAt);
      } else {
        setBackupNextRunAt(null);
      }
    } catch {
      setBackupLatest(null);
      setBackupNextRunAt(null);
    }
  };

  useEffect(() => {
    if (isAdmin) loadBackupData();
  }, [isAdmin]);

  const handleRunBackupVerification = async () => {
    setBackupRunning(true);
    setBackupError(null);
    try {
      const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
      const res = await fetch(`${BASE}/api/admin/backup-verification/run`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 409) {
          toast.info("A verification run is already in progress.");
        } else {
          setBackupError(data.error ?? "Verification failed");
          toast.error(data.error ?? "Backup verification failed");
        }
      } else {
        if (data.status === "pass") {
          toast.success("Backup verification passed.");
        } else {
          toast.error("Backup verification failed — see details below.");
        }
        await loadBackupData();
      }
    } catch {
      setBackupError("Network error — could not reach the server.");
      toast.error("Could not trigger backup verification.");
    } finally {
      setBackupRunning(false);
    }
  };

  const { data: emailProviderConfig, isLoading: isLoadingEmailProvider } = useGetEmailProviderConfig({
    query: { enabled: isAdmin, queryKey: getGetEmailProviderConfigQueryKey() },
  });
  const updateEmailProvider = useUpdateEmailProviderConfig();
  const clearEmailProvider = useClearEmailProviderConfig();
  const testEmailProvider = useTestEmailProviderSend();

  type ProviderType = "sendgrid" | "mailgun" | "postmark" | "resend" | "smtp";
  const [epProvider, setEpProvider] = useState<ProviderType>("sendgrid");
  const [epFromName, setEpFromName] = useState("");
  const [epFromAddress, setEpFromAddress] = useState("");
  const [epApiKey, setEpApiKey] = useState("");
  const [epMailgunDomain, setEpMailgunDomain] = useState("");
  const [epSmtpHost, setEpSmtpHost] = useState("");
  const [epSmtpPort, setEpSmtpPort] = useState("587");
  const [epSmtpUseTls, setEpSmtpUseTls] = useState(true);
  const [epSmtpUsername, setEpSmtpUsername] = useState("");
  const [epSmtpPassword, setEpSmtpPassword] = useState("");
  const [epTestRecipient, setEpTestRecipient] = useState("");
  const [epTestResult, setEpTestResult] = useState<{ ok: boolean; error?: string | null } | null>(null);

  useEffect(() => {
    if (emailProviderConfig && emailProviderConfig.source === "db") {
      setEpProvider(emailProviderConfig.provider as ProviderType);
      setEpFromName(emailProviderConfig.fromName ?? "");
      setEpFromAddress(emailProviderConfig.fromAddress ?? "");
      setEpMailgunDomain(emailProviderConfig.mailgunDomain ?? "");
      setEpSmtpHost(emailProviderConfig.smtpHost ?? "");
      setEpSmtpPort(String(emailProviderConfig.smtpPort ?? 587));
      setEpSmtpUseTls(emailProviderConfig.smtpUseTls ?? true);
      setEpSmtpUsername(emailProviderConfig.smtpUsername ?? "");
    } else if (emailProviderConfig && emailProviderConfig.source === "env") {
      setEpProvider("sendgrid");
      setEpFromName(emailProviderConfig.fromName ?? "");
      setEpFromAddress(emailProviderConfig.fromAddress ?? "");
    }
  }, [emailProviderConfig]);

  useEffect(() => {
    if (me?.email && !epTestRecipient) {
      setEpTestRecipient(me.email);
    }
  }, [me?.email]);

  const handleSaveEmailProvider = () => {
    const payload: Record<string, unknown> = {
      provider: epProvider,
      fromName: epFromName,
      fromAddress: epFromAddress,
    };
    if (epApiKey) payload.apiKey = epApiKey;
    if (epProvider === "mailgun") payload.mailgunDomain = epMailgunDomain;
    if (epProvider === "smtp") {
      payload.smtpHost = epSmtpHost;
      payload.smtpPort = parseInt(epSmtpPort) || 587;
      payload.smtpUseTls = epSmtpUseTls;
      payload.smtpUsername = epSmtpUsername;
      if (epSmtpPassword) payload.smtpPassword = epSmtpPassword;
    }
    updateEmailProvider.mutate(
      { data: payload as unknown as Parameters<typeof updateEmailProvider.mutate>[0]["data"] },
      {
        onSuccess: () => {
          toast.success("Email provider saved.");
          setEpApiKey("");
          setEpSmtpPassword("");
          queryClient.invalidateQueries({ queryKey: getGetEmailProviderConfigQueryKey() });
        },
        onError: (err: unknown) => toastError(err, "Failed to save email provider"),
      },
    );
  };

  const handleClearEmailProvider = () => {
    clearEmailProvider.mutate(undefined, {
      onSuccess: () => {
        toast.success("Email provider configuration cleared. Falling back to environment variables.");
        setEpApiKey("");
        setEpSmtpPassword("");
        queryClient.invalidateQueries({ queryKey: getGetEmailProviderConfigQueryKey() });
      },
      onError: (err: unknown) => toastError(err, "Failed to clear email provider"),
    });
  };

  const handleTestEmailProvider = () => {
    setEpTestResult(null);
    testEmailProvider.mutate(
      { data: { to: epTestRecipient } },
      {
        onSuccess: (result) => {
          setEpTestResult({ ok: result.ok });
          if (result.ok) toast.success(`Test email sent to ${epTestRecipient}!`);
        },
        onError: (err: unknown) => {
          const msg = getErrorMessage(err, "Test send failed");
          setEpTestResult({ ok: false, error: msg });
        },
      },
    );
  };

  function handleAddProduct() {
    const name = newProductName.trim();
    if (!name) return;
    createProduct.mutate(
      { data: { name } },
      {
        onSuccess: () => {
          setNewProductName("");
          queryClient.invalidateQueries({ queryKey: getListCatalogProductsQueryKey() });
          toast.success("Product added");
        },
        onError: (err: unknown) => toastError(err, "Failed to add product"),
      },
    );
  }

  function handleStartEdit(id: string, name: string) {
    setEditingProductId(id);
    setEditingProductName(name);
  }

  function handleSaveEdit(id: string) {
    const name = editingProductName.trim();
    if (!name) {
      toast.error("Product name cannot be empty");
      return;
    }
    updateProduct.mutate(
      { id, data: { name } },
      {
        onSuccess: () => {
          setEditingProductId(null);
          setEditingProductName("");
          queryClient.invalidateQueries({ queryKey: getListCatalogProductsQueryKey() });
          toast.success("Product renamed");
        },
        onError: (err: unknown) => toastError(err, "Failed to rename product"),
      },
    );
  }

  function handleDeleteProduct(id: string, name: string) {
    deleteProduct.mutate(
      { id },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: getListCatalogProductsQueryKey() });
          toast.success(`"${name}" removed from catalog`);
        },
        onError: (err: unknown) => toastError(err, "Failed to delete product"),
      },
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-4xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
        <p className="text-muted-foreground">Manage your integrations and account preferences.</p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <Card id="mailbox">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" /> Connected Mailbox
            </CardTitle>
            <CardDescription>
              Connect your email account to send deal registrations directly to suppliers.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MailboxOAuthStatusBadges isAdmin={isAdmin} oauthStatus={oauthStatus} />
            {isLoadingMailbox || isLoadingMe ? (
              <div className="space-y-4">
                <Skeleton className="h-16 w-full" />
              </div>
            ) : mailbox?.connected ? (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 border rounded-lg bg-muted/20">
                <div className="flex items-center gap-4">
                  <div className="h-10 w-10 bg-primary/10 text-primary rounded-full flex items-center justify-center">
                    <Mail className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-medium">{mailbox.email || "Connected"}</div>
                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                      <span className="capitalize">{mailbox.provider}</span>
                      <span>•</span>
                      <span>Connected {formatDateTime(mailbox.connectedAt)}</span>
                    </div>
                  </div>
                </div>
                <Button variant="outline" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={handleDisconnect} disabled={disconnectMailbox.isPending}>
                  {disconnectMailbox.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogOut className="mr-2 h-4 w-4" />}
                  Disconnect
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800 dark:text-amber-400">Mailbox not connected</h4>
                    <p className="text-sm text-amber-700/80 dark:text-amber-500/80 mt-1">
                      You must connect your mailbox to send deal registrations.
                    </p>
                  </div>
                </div>
                {isAdmin && oauthRedirects && (!oauthRedirects.googleConfigured || !oauthRedirects.microsoftConfigured) && (
                  <div className="p-4 border rounded-lg bg-muted/20 space-y-3" data-testid="oauth-redirect-uris">
                    <div>
                      <h4 className="font-medium text-sm">OAuth redirect URIs</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Register the URI for your provider in the Google Cloud Console (Authorized
                        redirect URIs) or Azure App Registration (Redirect URI), then set the
                        client ID and secret as environment variables for this app.
                      </p>
                    </div>
                    {!oauthRedirects.googleConfigured && (
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-muted-foreground">Google</div>
                        <div className="flex items-center gap-2">
                          <code
                            className="flex-1 text-xs font-mono bg-background border rounded px-2 py-1.5 break-all"
                            data-testid="text-google-redirect-uri"
                          >
                            {oauthRedirects.google}
                          </code>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() => handleCopyRedirect(oauthRedirects.google, "Google")}
                            data-testid="button-copy-google-redirect"
                            aria-label="Copy Google redirect URI"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                    {!oauthRedirects.microsoftConfigured && (
                      <div className="space-y-1">
                        <div className="text-xs font-medium text-muted-foreground">Microsoft</div>
                        <div className="flex items-center gap-2">
                          <code
                            className="flex-1 text-xs font-mono bg-background border rounded px-2 py-1.5 break-all"
                            data-testid="text-microsoft-redirect-uri"
                          >
                            {oauthRedirects.microsoft}
                          </code>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() => handleCopyRedirect(oauthRedirects.microsoft, "Microsoft")}
                            data-testid="button-copy-microsoft-redirect"
                            aria-label="Copy Microsoft redirect URI"
                          >
                            <Copy className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-4 pt-2">
                  <Button variant="outline" onClick={handleConnectGoogle} className="flex-1 h-12" disabled={oauthPending !== null}>
                    {oauthPending === "google" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        <path d="M1 1h22v22H1z" fill="none" />
                      </svg>
                    )}
                    {oauthPending === "google" ? "Connecting…" : "Connect Google Workspace"}
                  </Button>
                  <Button variant="outline" onClick={handleConnectMicrosoft} className="flex-1 h-12" disabled={oauthPending !== null}>
                    {oauthPending === "microsoft" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21">
                        <path fill="#f25022" d="M1 1h9v9H1z" />
                        <path fill="#00a4ef" d="M11 1h9v9h-9z" />
                        <path fill="#7fba00" d="M1 11h9v9H1z" />
                        <path fill="#ffb900" d="M11 11h9v9h-9z" />
                      </svg>
                    )}
                    {oauthPending === "microsoft" ? "Connecting…" : "Connect Microsoft 365"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {isAdmin && pollHealth && pollHealth.mailboxes && pollHealth.mailboxes.length > 0 && (
          <Card data-testid="poll-health-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5" /> Inbound Reply Polling
              </CardTitle>
              <CardDescription>
                Status of automatic supplier reply detection. TDM polls connected mailboxes every 3 minutes.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {pollHealth.mailboxes.map((item: MailboxPollHealth) => {
                const healthy = item.isPolling && !item.lastPollError;
                return (
                  <div key={item.email} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border bg-muted/20">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${healthy ? "bg-green-500" : item.lastPollError ? "bg-red-500" : "bg-amber-400"}`} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{item.email}</div>
                        <div className="text-xs text-muted-foreground capitalize">{item.provider}</div>
                      </div>
                    </div>
                    <div className="flex flex-col sm:items-end text-xs text-muted-foreground gap-0.5">
                      {item.lastPolledAt ? (
                        <span>Last polled {formatDateTime(item.lastPolledAt)}</span>
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400">Not yet polled</span>
                      )}
                      {item.lastPollError && (
                        <span className="text-destructive truncate max-w-xs" title={item.lastPollError}>
                          Error: {item.lastPollError.slice(0, 80)}{item.lastPollError.length > 80 ? "…" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card id="email-provider">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ServerCog className="h-5 w-5" /> Email Provider
              </CardTitle>
              <CardDescription>
                Configure the system email provider used for transactional and support emails. Secrets are encrypted at rest and never shown in plaintext after saving.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingEmailProvider ? (
                <Skeleton className="h-24 w-full" />
              ) : (
                <>
                  <div className="flex items-center gap-2 text-sm">
                    {emailProviderConfig?.source === "db" && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 dark:border-green-900 dark:bg-green-950/30 dark:text-green-400">
                        <CheckCircle2 className="h-3.5 w-3.5" /> Using saved settings
                      </span>
                    )}
                    {emailProviderConfig?.source === "env" && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-400">
                        <AlertCircle className="h-3.5 w-3.5" /> Using environment variables
                      </span>
                    )}
                    {emailProviderConfig?.source === "none" && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-medium text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-400">
                        <XCircle className="h-3.5 w-3.5" /> Not configured
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="ep-provider">Provider</Label>
                      <Select value={epProvider} onValueChange={(v) => { setEpProvider(v as ProviderType); setEpApiKey(""); setEpSmtpPassword(""); }}>
                        <SelectTrigger id="ep-provider">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="sendgrid">SendGrid</SelectItem>
                          <SelectItem value="mailgun">Mailgun</SelectItem>
                          <SelectItem value="postmark">Postmark</SelectItem>
                          <SelectItem value="resend">Resend</SelectItem>
                          <SelectItem value="smtp">Generic SMTP</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="ep-from-name">From name</Label>
                      <Input
                        id="ep-from-name"
                        placeholder="TDM Registration App"
                        value={epFromName}
                        onChange={(e) => setEpFromName(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="ep-from-address">From address</Label>
                      <Input
                        id="ep-from-address"
                        type="email"
                        placeholder="no-reply@example.com"
                        value={epFromAddress}
                        onChange={(e) => setEpFromAddress(e.target.value)}
                      />
                    </div>
                  </div>

                  {epProvider !== "smtp" && (
                    <div className="space-y-1.5">
                      <Label htmlFor="ep-api-key">
                        {epProvider === "sendgrid" && "SendGrid API key"}
                        {epProvider === "mailgun" && "Mailgun API key"}
                        {epProvider === "postmark" && "Postmark server token"}
                        {epProvider === "resend" && "Resend API key"}
                      </Label>
                      <Input
                        id="ep-api-key"
                        type="password"
                        placeholder={
                          emailProviderConfig?.source === "db" &&
                          emailProviderConfig.provider === epProvider &&
                          emailProviderConfig.hasApiKey
                            ? "••••••••  (saved — leave blank to keep)"
                            : "Enter API key…"
                        }
                        value={epApiKey}
                        onChange={(e) => setEpApiKey(e.target.value)}
                        autoComplete="new-password"
                      />
                    </div>
                  )}

                  {epProvider === "mailgun" && (
                    <div className="space-y-1.5">
                      <Label htmlFor="ep-mailgun-domain">Mailgun domain</Label>
                      <Input
                        id="ep-mailgun-domain"
                        placeholder="mg.yourdomain.com"
                        value={epMailgunDomain}
                        onChange={(e) => setEpMailgunDomain(e.target.value)}
                      />
                    </div>
                  )}

                  {epProvider === "smtp" && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div className="sm:col-span-2 space-y-1.5">
                          <Label htmlFor="ep-smtp-host">SMTP host</Label>
                          <Input
                            id="ep-smtp-host"
                            placeholder="smtp.example.com"
                            value={epSmtpHost}
                            onChange={(e) => setEpSmtpHost(e.target.value)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="ep-smtp-port">Port</Label>
                          <Input
                            id="ep-smtp-port"
                            type="number"
                            placeholder="587"
                            value={epSmtpPort}
                            onChange={(e) => setEpSmtpPort(e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch
                          id="ep-smtp-tls"
                          checked={epSmtpUseTls}
                          onCheckedChange={setEpSmtpUseTls}
                        />
                        <Label htmlFor="ep-smtp-tls" className="font-normal cursor-pointer">Use TLS / STARTTLS</Label>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label htmlFor="ep-smtp-username">Username</Label>
                          <Input
                            id="ep-smtp-username"
                            placeholder="user@example.com"
                            value={epSmtpUsername}
                            onChange={(e) => setEpSmtpUsername(e.target.value)}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <Label htmlFor="ep-smtp-password">Password</Label>
                          <Input
                            id="ep-smtp-password"
                            type="password"
                            placeholder={
                              emailProviderConfig?.source === "db" &&
                              emailProviderConfig.provider === "smtp" &&
                              emailProviderConfig.hasSmtpPassword
                                ? "••••••••  (saved — leave blank to keep)"
                                : "Enter password…"
                            }
                            value={epSmtpPassword}
                            onChange={(e) => setEpSmtpPassword(e.target.value)}
                            autoComplete="new-password"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {(() => {
                    const apiKeyRequired = ["sendgrid", "mailgun", "postmark", "resend"].includes(epProvider);
                    const existingKeyKept =
                      apiKeyRequired &&
                      emailProviderConfig?.source === "db" &&
                      emailProviderConfig.provider === epProvider &&
                      !!emailProviderConfig.hasApiKey;
                    const apiKeyMissing = apiKeyRequired && !epApiKey && !existingKeyKept;
                    const mailgunDomainKept =
                      epProvider === "mailgun" &&
                      emailProviderConfig?.source === "db" &&
                      emailProviderConfig.provider === "mailgun" &&
                      !!emailProviderConfig.mailgunDomain;
                    const mailgunDomainMissing = epProvider === "mailgun" && !epMailgunDomain && !mailgunDomainKept;
                    const smtpHostMissing = epProvider === "smtp" && !epSmtpHost;
                    const canSave =
                      !!epFromAddress &&
                      !apiKeyMissing &&
                      !mailgunDomainMissing &&
                      !smtpHostMissing;
                    return (
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Button
                      onClick={handleSaveEmailProvider}
                      disabled={updateEmailProvider.isPending || !canSave}
                      size="sm"
                      title={!canSave ? "Fill in all required fields to save" : undefined}
                    >
                      {updateEmailProvider.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save provider
                    </Button>
                    {emailProviderConfig?.source === "db" && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleClearEmailProvider}
                        disabled={clearEmailProvider.isPending}
                        className="text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                      >
                        {clearEmailProvider.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Clear saved config
                      </Button>
                    )}
                  </div>
                    );
                  })()}

                  <div className="border-t pt-4 space-y-2">
                    <h4 className="text-sm font-medium flex items-center gap-2">
                      <Send className="h-4 w-4" /> Send test email
                    </h4>
                    <p className="text-xs text-muted-foreground">
                      Sends a real email through the currently-saved provider to verify the configuration.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <Input
                        type="email"
                        placeholder="recipient@example.com"
                        value={epTestRecipient}
                        onChange={(e) => { setEpTestRecipient(e.target.value); setEpTestResult(null); }}
                        className="flex-1"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleTestEmailProvider}
                        disabled={testEmailProvider.isPending || !epTestRecipient || emailProviderConfig?.source === "none"}
                      >
                        {testEmailProvider.isPending ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="mr-2 h-4 w-4" />
                        )}
                        Send test
                      </Button>
                    </div>
                    {epTestResult !== null && (
                      <div className={`flex items-start gap-2 text-sm rounded-lg px-3 py-2 ${epTestResult.ok ? "bg-green-50 text-green-700 border border-green-200 dark:bg-green-950/30 dark:text-green-400 dark:border-green-900" : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/30 dark:text-red-400 dark:border-red-900"}`}>
                        {epTestResult.ok ? (
                          <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                        ) : (
                          <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                        )}
                        <span>
                          {epTestResult.ok
                            ? `Test email sent to ${epTestRecipient} successfully.`
                            : (epTestResult.error ?? "Test send failed. Check your provider configuration.")}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card id="backup">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" /> Backup Verification
              </CardTitle>
              <CardDescription>
                A daily job dumps the live database, verifies the dump is valid and restorable, and runs sanity checks on table presence and row counts. If anything fails, all admins receive an alert email.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {backupLatest === undefined ? (
                <Skeleton className="h-20 w-full" />
              ) : backupLatest === null ? (
                <div className="p-4 border border-muted rounded-lg bg-muted/20 text-sm text-muted-foreground">
                  No verification runs recorded yet. The first run will occur automatically within a minute of the server starting, or you can trigger one manually below.
                </div>
              ) : (
                <div className={`p-4 border rounded-lg flex flex-col gap-3 ${
                  backupLatest.status === "pass"
                    ? "border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950/30"
                    : backupLatest.status === "running"
                    ? "border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30"
                    : "border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/30"
                }`}>
                  <div className="flex items-center gap-2">
                    {backupLatest.status === "pass" && <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0" />}
                    {backupLatest.status === "fail" && <XCircle className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />}
                    {backupLatest.status === "running" && <Loader2 className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 animate-spin" />}
                    <span className={`font-semibold text-sm ${
                      backupLatest.status === "pass" ? "text-green-800 dark:text-green-300"
                      : backupLatest.status === "running" ? "text-blue-800 dark:text-blue-300"
                      : "text-red-800 dark:text-red-300"
                    }`}>
                      {backupLatest.status === "pass" ? "Last verification passed"
                        : backupLatest.status === "running" ? "Verification in progress…"
                        : "Last verification failed"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <Clock className="h-3.5 w-3.5 shrink-0" />
                      <div>
                        <div className="font-medium text-foreground">Run time</div>
                        <div>{formatDateTime(backupLatest.startedAt)}</div>
                      </div>
                    </div>
                    {backupLatest.durationMs !== null && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <RefreshCw className="h-3.5 w-3.5 shrink-0" />
                        <div>
                          <div className="font-medium text-foreground">Duration</div>
                          <div>{(backupLatest.durationMs / 1000).toFixed(1)}s</div>
                        </div>
                      </div>
                    )}
                    {backupLatest.dumpSizeBytes !== null && backupLatest.dumpSizeBytes > 0 && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <HardDrive className="h-3.5 w-3.5 shrink-0" />
                        <div>
                          <div className="font-medium text-foreground">Dump size</div>
                          <div>{(backupLatest.dumpSizeBytes / 1024).toFixed(1)} KB</div>
                        </div>
                      </div>
                    )}
                    {backupLatest.tableCount !== null && backupLatest.tableCount > 0 && (
                      <div className="flex items-center gap-1.5 text-muted-foreground">
                        <Database className="h-3.5 w-3.5 shrink-0" />
                        <div>
                          <div className="font-medium text-foreground">Tables in dump</div>
                          <div>{backupLatest.tableCount}</div>
                        </div>
                      </div>
                    )}
                  </div>
                  {backupLatest.errorMessage && (
                    <div className="text-xs text-red-700 dark:text-red-400 font-mono bg-red-100 dark:bg-red-950/50 rounded px-2 py-1.5 break-all">
                      {backupLatest.errorMessage}
                    </div>
                  )}
                  {backupLatest.status === "fail" && !backupLatest.errorMessage && (
                    <p className="text-xs text-red-700 dark:text-red-400">
                      Check the run history below for details. Resolve the issue and trigger a manual run to confirm recovery.
                    </p>
                  )}
                </div>
              )}

              {backupNextRunAt && (
                <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Clock className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    <span className="font-medium text-foreground">Next run:</span>{" "}
                    {formatRelativeNextRun(backupNextRunAt)} (around {formatDateTime(backupNextRunAt)})
                  </span>
                </div>
              )}

              {backupError && (
                <div className="flex items-start gap-2 text-sm text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded-lg px-3 py-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{backupError}</span>
                </div>
              )}

              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRunBackupVerification}
                  disabled={backupRunning || backupLatest?.status === "running"}
                >
                  {backupRunning ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  {backupRunning ? "Running…" : "Run verification now"}
                </Button>
              </div>

              <div className="pt-2 border-t space-y-2">
                <Label htmlFor="backup-interval-hours" className="text-sm font-medium">
                  Schedule interval
                </Label>
                <p className="text-xs text-muted-foreground">
                  How often the backup verification job runs automatically. Allowed range: {adminSettings?.backupVerifyIntervalHoursMin ?? 1}–{adminSettings?.backupVerifyIntervalHoursMax ?? 168} hours.
                </p>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-muted-foreground">Run every</span>
                  <Input
                    id="backup-interval-hours"
                    type="number"
                    min={adminSettings?.backupVerifyIntervalHoursMin ?? 1}
                    max={adminSettings?.backupVerifyIntervalHoursMax ?? 168}
                    step={1}
                    value={backupIntervalInput}
                    onChange={(e) => setBackupIntervalInput(e.target.value)}
                    disabled={isLoadingAdminSettings || updateAdminSettings.isPending}
                    className="w-24"
                  />
                  <span className="text-sm text-muted-foreground">hour(s)</span>
                  <Button
                    size="sm"
                    onClick={handleSaveBackupInterval}
                    disabled={
                      isLoadingAdminSettings ||
                      updateAdminSettings.isPending ||
                      backupIntervalInput.trim() === String(adminSettings?.backupVerifyIntervalHours ?? "")
                    }
                  >
                    {updateAdminSettings.isPending ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : null}
                    Save
                  </Button>
                  {adminSettings &&
                    adminSettings.backupVerifyIntervalHours !== adminSettings.backupVerifyIntervalHoursDefault && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetBackupInterval}
                        disabled={updateAdminSettings.isPending}
                      >
                        Reset to default ({adminSettings.backupVerifyIntervalHoursDefault}h)
                      </Button>
                    )}
                </div>
                {adminSettings && (
                  <p className="text-xs text-muted-foreground">
                    Currently set to <strong>every {adminSettings.backupVerifyIntervalHours} hour{adminSettings.backupVerifyIntervalHours === 1 ? "" : "s"}</strong>
                    {adminSettings.backupVerifyIntervalHours === adminSettings.backupVerifyIntervalHoursDefault
                      ? " (default)."
                      : ` (default is ${adminSettings.backupVerifyIntervalHoursDefault} hours).`}
                  </p>
                )}
              </div>

              {backupHistory.length > 0 && (
                <Collapsible open={backupHistoryOpen} onOpenChange={setBackupHistoryOpen}>
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center gap-2 text-muted-foreground hover:text-foreground -ml-1 h-8 px-2">
                      <ChevronDown className={`h-4 w-4 transition-transform ${backupHistoryOpen ? "rotate-180" : ""}`} />
                      Run history ({backupHistory.length})
                    </Button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-2 rounded-lg border bg-muted/20 divide-y">
                      {backupHistory.map((run) => (
                        <div key={run.id} className="flex items-start gap-3 px-3 py-2 text-xs">
                          {run.status === "pass" && <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mt-0.5 shrink-0" />}
                          {run.status === "fail" && <XCircle className="h-3.5 w-3.5 text-red-500 mt-0.5 shrink-0" />}
                          {run.status === "running" && <Loader2 className="h-3.5 w-3.5 text-blue-500 mt-0.5 shrink-0 animate-spin" />}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <span className={`font-medium ${run.status === "pass" ? "text-green-700 dark:text-green-400" : run.status === "running" ? "text-blue-700 dark:text-blue-400" : "text-red-700 dark:text-red-400"}`}>
                                {run.status.toUpperCase()}
                              </span>
                              <span className="text-muted-foreground">{formatDateTime(run.startedAt)}</span>
                              {run.durationMs !== null && (
                                <span className="text-muted-foreground">{(run.durationMs / 1000).toFixed(1)}s</span>
                              )}
                              {run.dumpSizeBytes !== null && run.dumpSizeBytes > 0 && (
                                <span className="text-muted-foreground">{(run.dumpSizeBytes / 1024).toFixed(0)} KB</span>
                              )}
                              <span className="text-muted-foreground/70 capitalize">{run.triggeredBy}</span>
                            </div>
                            {run.errorMessage && (
                              <div className="text-red-600 dark:text-red-400 mt-1 font-mono break-all">{run.errorMessage}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card data-testid="card-scheduled-reports">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" /> Scheduled analytics reports
              </CardTitle>
              <CardDescription>
                Email a summary of the analytics reports on a recurring schedule. Uses the same aggregation as the Reports page over the most recent 7- or 30-day window.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingAdminSettings ? (
                <Skeleton className="h-32 w-full" />
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="reports-frequency" className="text-sm font-medium">Frequency</Label>
                      <Select
                        value={reportsFrequency}
                        onValueChange={(v) => setReportsFrequency(v as "off" | "weekly" | "monthly")}
                      >
                        <SelectTrigger id="reports-frequency" data-testid="select-reports-frequency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="off">Off</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="reports-hour" className="text-sm font-medium">Hour (UTC)</Label>
                      <Input
                        id="reports-hour"
                        type="number"
                        min={0}
                        max={23}
                        value={String(reportsHourUtc)}
                        onChange={(e) => setReportsHourUtc(Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)))}
                        disabled={reportsFrequency === "off"}
                        data-testid="input-reports-hour"
                      />
                    </div>
                    {reportsFrequency === "weekly" && (
                      <div className="space-y-1.5">
                        <Label htmlFor="reports-day-of-week" className="text-sm font-medium">Day of week</Label>
                        <Select
                          value={String(reportsDayOfWeek)}
                          onValueChange={(v) => setReportsDayOfWeek(parseInt(v, 10))}
                        >
                          <SelectTrigger id="reports-day-of-week" data-testid="select-reports-day-of-week">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Sunday</SelectItem>
                            <SelectItem value="1">Monday</SelectItem>
                            <SelectItem value="2">Tuesday</SelectItem>
                            <SelectItem value="3">Wednesday</SelectItem>
                            <SelectItem value="4">Thursday</SelectItem>
                            <SelectItem value="5">Friday</SelectItem>
                            <SelectItem value="6">Saturday</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {reportsFrequency === "monthly" && (
                      <div className="space-y-1.5">
                        <Label htmlFor="reports-day-of-month" className="text-sm font-medium">Day of month (1–28)</Label>
                        <Input
                          id="reports-day-of-month"
                          type="number"
                          min={1}
                          max={28}
                          value={String(reportsDayOfMonth)}
                          onChange={(e) => setReportsDayOfMonth(Math.max(1, Math.min(28, parseInt(e.target.value, 10) || 1)))}
                          data-testid="input-reports-day-of-month"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="reports-recipients" className="text-sm font-medium">
                      Additional recipients
                    </Label>
                    <Textarea
                      id="reports-recipients"
                      value={reportsRecipientsText}
                      onChange={(e) => setReportsRecipientsText(e.target.value)}
                      placeholder="alice@example.com, bob@example.com"
                      rows={3}
                      data-testid="textarea-reports-recipients"
                    />
                    <p className="text-xs text-muted-foreground">
                      Separate emails with commas, semicolons, spaces, or newlines. Max {adminSettings?.reportsScheduleMaxRecipients ?? 50}.
                    </p>
                  </div>

                  <div className="flex items-center justify-between py-1">
                    <div>
                      <Label htmlFor="reports-include-admins" className="text-sm font-medium">
                        Include all admins + support email
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        When on, the report is also sent to every admin user and the configured support email.
                      </p>
                    </div>
                    <Switch
                      id="reports-include-admins"
                      checked={reportsIncludeAdmins}
                      onCheckedChange={setReportsIncludeAdmins}
                      data-testid="switch-reports-include-admins"
                    />
                  </div>

                  {(adminSettings?.reportsScheduleNextScheduledAt || adminSettings?.reportsScheduleLastSentAt) && (
                    <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs space-y-1">
                      {adminSettings?.reportsScheduleNextScheduledAt && (
                        <div>
                          <span className="font-medium">Next scheduled:</span>{" "}
                          <span className="text-muted-foreground">
                            {formatDateTime(adminSettings.reportsScheduleNextScheduledAt)}
                          </span>
                        </div>
                      )}
                      {adminSettings?.reportsScheduleLastSentAt && (
                        <div>
                          <span className="font-medium">Last sent:</span>{" "}
                          <span className="text-muted-foreground">
                            {formatDateTime(adminSettings.reportsScheduleLastSentAt)}
                          </span>
                          {adminSettings.reportsScheduleLastStatus && (
                            <span className={`ml-2 ${adminSettings.reportsScheduleLastStatus.ok ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}`}>
                              {adminSettings.reportsScheduleLastStatus.ok ? "OK" : "Failed"}
                              {adminSettings.reportsScheduleLastStatus.error && ` — ${adminSettings.reportsScheduleLastStatus.error}`}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      size="sm"
                      onClick={handleSaveReportsSchedule}
                      disabled={updateAdminSettings.isPending}
                      data-testid="button-save-reports-schedule"
                    >
                      {updateAdminSettings.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save schedule
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRunReportNow}
                      disabled={runReportNow.isPending}
                      data-testid="button-run-report-now"
                    >
                      {runReportNow.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-2 h-3.5 w-3.5" />
                      )}
                      Send a test report now
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <KeyRound className="h-5 w-5" /> Credential expiry thresholds
              </CardTitle>
              <CardDescription>
                Configure how long credentials (encryption key, mailbox OAuth tokens, Salesforce credentials) remain valid before warnings are sent. Settings stored here override the corresponding environment variables.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingAdminSettings ? (
                <Skeleton className="h-32 w-full" />
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
                    <div className="space-y-1.5">
                      <label htmlFor="credential-warn-days" className="text-sm font-medium">
                        Warn-ahead window (days)
                      </label>
                      <Input
                        id="credential-warn-days"
                        type="number"
                        min={adminSettings?.credentialWarnDaysMin ?? 1}
                        max={adminSettings?.credentialWarnDaysMax ?? 365}
                        value={credentialWarnInput}
                        onChange={(e) => setCredentialWarnInput(e.target.value)}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">
                        Default: {adminSettings?.credentialWarnDaysDefault ?? 14} days
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="encryption-key-max-age" className="text-sm font-medium">
                        Encryption key max age (days)
                      </label>
                      <Input
                        id="encryption-key-max-age"
                        type="number"
                        min={adminSettings?.credentialMaxAgeDaysMin ?? 1}
                        max={adminSettings?.credentialMaxAgeDaysMax ?? 3650}
                        value={encryptionKeyMaxAgeInput}
                        onChange={(e) => setEncryptionKeyMaxAgeInput(e.target.value)}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">
                        Default: {adminSettings?.encryptionKeyMaxAgeDaysDefault ?? 365} days
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="mailbox-token-max-age" className="text-sm font-medium">
                        Mailbox token max age (days)
                      </label>
                      <Input
                        id="mailbox-token-max-age"
                        type="number"
                        min={adminSettings?.credentialMaxAgeDaysMin ?? 1}
                        max={adminSettings?.credentialMaxAgeDaysMax ?? 3650}
                        value={mailboxTokenMaxAgeInput}
                        onChange={(e) => setMailboxTokenMaxAgeInput(e.target.value)}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">
                        Default: {adminSettings?.mailboxTokenMaxAgeDaysDefault ?? 180} days
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="sf-credential-max-age" className="text-sm font-medium">
                        Salesforce credential max age (days)
                      </label>
                      <Input
                        id="sf-credential-max-age"
                        type="number"
                        min={adminSettings?.credentialMaxAgeDaysMin ?? 1}
                        max={adminSettings?.credentialMaxAgeDaysMax ?? 3650}
                        value={sfCredentialMaxAgeInput}
                        onChange={(e) => setSfCredentialMaxAgeInput(e.target.value)}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">
                        Default: {adminSettings?.sfCredentialMaxAgeDaysDefault ?? 730} days
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      size="sm"
                      onClick={handleSaveCredentialThresholds}
                      disabled={updateAdminSettings.isPending}
                    >
                      {updateAdminSettings.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save thresholds
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetCredentialThresholds}
                      disabled={updateAdminSettings.isPending}
                    >
                      <RotateCcw className="mr-2 h-3.5 w-3.5" />
                      Reset to defaults
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Flame className="h-5 w-5" /> Stale deal thresholds
              </CardTitle>
              <CardDescription>
                Configure when a deal registration is considered stale. Age is measured from the most recent registration email sent (or supplier reply received, whichever is newer).
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingAdminSettings ? (
                <Skeleton className="h-24 w-full" />
              ) : (
                <>
                  <div className="flex flex-wrap items-start gap-4">
                    <div className="flex items-center gap-2 rounded-md border border-yellow-200 bg-yellow-50 dark:bg-yellow-950/20 dark:border-yellow-900 px-3 py-1.5 text-xs font-medium text-yellow-700 dark:text-yellow-400">
                      <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                      Yellow (warning): ≥ {adminSettings?.staleYellowDays ?? "…"} days
                    </div>
                    <div className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400">
                      <Flame className="h-3.5 w-3.5 shrink-0" />
                      Red (overdue): ≥ {adminSettings?.staleRedDays ?? "…"} days
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md">
                    <div className="space-y-1.5">
                      <label htmlFor="stale-yellow" className="text-sm font-medium">
                        Yellow threshold (days)
                      </label>
                      <Input
                        id="stale-yellow"
                        type="number"
                        min={adminSettings?.staleYellowDaysMin ?? 1}
                        max={adminSettings?.staleYellowDaysMax ?? 365}
                        value={staleYellowInput}
                        onChange={(e) => setStaleYellowInput(e.target.value)}
                        placeholder="5"
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">
                        Default: {adminSettings?.staleYellowDaysDefault ?? 5} days
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="stale-red" className="text-sm font-medium">
                        Red threshold (days)
                      </label>
                      <Input
                        id="stale-red"
                        type="number"
                        min={adminSettings?.staleRedDaysMin ?? 1}
                        max={adminSettings?.staleRedDaysMax ?? 365}
                        value={staleRedInput}
                        onChange={(e) => setStaleRedInput(e.target.value)}
                        placeholder="10"
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">
                        Default: {adminSettings?.staleRedDaysDefault ?? 10} days
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Button
                      size="sm"
                      onClick={handleSaveStaleThresholds}
                      disabled={updateAdminSettings.isPending}
                    >
                      {updateAdminSettings.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save thresholds
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetStaleThresholds}
                      disabled={updateAdminSettings.isPending}
                    >
                      <RotateCcw className="mr-2 h-3.5 w-3.5" />
                      Reset to defaults
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" /> Copy me on registrations
            </CardTitle>
            <CardDescription>
              Receive a copy of every deal-registration email you send to keep your own paper trail.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isLoadingCopyPref ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <>
                <div className="flex items-center justify-between py-1">
                  <div>
                    <Label htmlFor="copy-enabled" className="text-sm font-medium">
                      Copy me on sends
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      A copy will be sent to <span className="font-medium">{me?.email}</span> for each registration email you send.
                    </p>
                  </div>
                  <Switch
                    id="copy-enabled"
                    checked={copyEnabled}
                    onCheckedChange={setCopyEnabled}
                    data-testid="switch-copy-enabled"
                  />
                </div>
                {copyEnabled && (
                  <div className="space-y-3 pt-2 border-t">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="copy-mode" className="text-sm">Copy type</Label>
                        <Select value={copyMode} onValueChange={(v) => setCopyMode(v as "cc" | "bcc")}>
                          <SelectTrigger id="copy-mode" data-testid="select-copy-mode">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="bcc">BCC (hidden from supplier)</SelectItem>
                            <SelectItem value="cc">CC (visible to supplier)</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {copyMode === "cc"
                            ? "The supplier will see your email in the CC field."
                            : "Your copy is hidden — the supplier won't see it."}
                        </p>
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="copy-scope" className="text-sm">When to copy</Label>
                        <Select value={copyScope} onValueChange={(v) => setCopyScope(v as "initial" | "all")}>
                          <SelectTrigger id="copy-scope" data-testid="select-copy-scope">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="initial">Initial registration only</SelectItem>
                            <SelectItem value="all">Initial + follow-ups</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-1">
                  <Button
                    onClick={handleSaveCopyPref}
                    disabled={updateCopyPref.isPending}
                    size="sm"
                    data-testid="button-save-copy-pref"
                  >
                    {updateCopyPref.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save preference
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LifeBuoy className="h-5 w-5" /> Support ticket emails
            </CardTitle>
            <CardDescription>
              Get notified by email when your support tickets change status or receive a reply.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoadingSupportEmailPref ? (
              <Skeleton className="h-12 w-full" />
            ) : (
              <div className="flex items-center justify-between py-1">
                <div>
                  <Label htmlFor="support-email-enabled" className="text-sm font-medium">
                    Email me about my support tickets
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    When on, we'll email <span className="font-medium">{me?.email}</span> when an admin updates a ticket's status or replies to it.
                  </p>
                </div>
                <Switch
                  id="support-email-enabled"
                  checked={supportEmailEnabled}
                  onCheckedChange={handleSaveSupportEmailPref}
                  disabled={updateSupportEmailPref.isPending}
                  data-testid="switch-support-email-enabled"
                />
              </div>
            )}
          </CardContent>
        </Card>

        <Card id="salesforce">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" /> Salesforce Integration
            </CardTitle>
            <CardDescription>
              Connect your Salesforce org to enable opportunity import, push, and supplier directory.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {sfStatus?.lastAuthError && (
              <div className="mb-3 p-3 border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 rounded-lg flex items-start gap-3">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-500 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-800 dark:text-amber-300">
                  <div className="font-medium">
                    Last auth check failed
                    {sfStatus.lastAuthCheck
                      ? ` at ${formatDateTime(sfStatus.lastAuthCheck)}`
                      : ""}
                    {" "}— edit credentials to reconnect.
                  </div>
                  <div className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-1 font-mono break-all">
                    {sfStatus.lastAuthError}
                  </div>
                </div>
              </div>
            )}
            {isLoadingSf || isLoadingSfConfig ? (
              <Skeleton className="h-16 w-full" />
            ) : sfConnected ? (
              <div className="space-y-3">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 border rounded-lg bg-muted/20">
                  <div className="flex items-center gap-4">
                    <div className="h-10 w-10 bg-emerald-500/10 text-emerald-600 rounded-full flex items-center justify-center">
                      <ShieldCheck className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="font-medium flex items-center gap-2 flex-wrap">
                        Salesforce
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/10 text-emerald-600 border border-emerald-200 dark:border-emerald-800">
                          Connected
                        </span>
                        {sfStatus?.environment && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-muted text-muted-foreground border capitalize">
                            {sfStatus.environment}
                          </span>
                        )}
                        {sfConfig?.source === "db" && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800"
                            title="These credentials are saved to your account only — other users have their own connections."
                          >
                            Personal connection
                          </span>
                        )}
                        {sfConfig?.source === "env" && (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800"
                            title="No personal connection saved — falling back to the platform-wide credentials shared by everyone in this workspace."
                          >
                            Shared organization connection
                          </span>
                        )}
                      </div>
                      {sfConfig?.username && (
                        <div className="text-sm text-muted-foreground mt-0.5">
                          {sfConfig.username}
                        </div>
                      )}
                      {sfStatus?.instanceUrl && (
                        <div className="text-xs text-muted-foreground font-mono mt-0.5">
                          {sfStatus.instanceUrl}
                        </div>
                      )}
                      {sfConfig?.source === "env" && (
                        <div className="text-xs text-muted-foreground mt-1 max-w-md">
                          You're using the shared organization Salesforce
                          connection (set via environment variables). Anyone
                          else without a personal connection sees the same
                          Salesforce data. Connect your own credentials to use
                          a private connection scoped to your account.
                        </div>
                      )}
                      {sfConfig?.updatedByEmail && sfConfig.source === "db" && (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Last updated by {sfConfig.updatedByEmail}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestSalesforceConnection}
                      disabled={testSalesforceMutation.isPending}
                      data-testid="button-test-salesforce-connection"
                    >
                      {testSalesforceMutation.isPending ? (
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Plug className="mr-2 h-3.5 w-3.5" />
                      )}
                      Test Connection
                    </Button>
                    {sfIsEnvConfig ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setShowConnectDialog(true)}
                      >
                        <Plug className="mr-2 h-3.5 w-3.5" />
                        Use personal connection
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowConnectDialog(true)}
                        >
                          <Pencil className="mr-2 h-3.5 w-3.5" />
                          Edit Credentials
                        </Button>
                        <DisconnectSalesforceButton
                          onDisconnected={() => {
                            queryClient.invalidateQueries({ queryKey: getGetSalesforceStatusQueryKey() });
                            queryClient.invalidateQueries({ queryKey: getGetSalesforceConfigQueryKey() });
                          }}
                        />
                      </>
                    )}
                  </div>
                </div>

                {sfTestResult && (
                  <div
                    className={`flex items-start gap-2 p-3 rounded-lg border text-sm ${
                      sfTestResult.success
                        ? "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300"
                        : "bg-destructive/5 border-destructive/20 text-destructive"
                    }`}
                    data-testid={`sf-test-result-${sfTestResult.success ? "ok" : "fail"}`}
                  >
                    {sfTestResult.success ? (
                      <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                    ) : (
                      <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                    )}
                    <div className="min-w-0">
                      {sfTestResult.success ? (
                        <>
                          <span className="font-medium">
                            Connected{sfTestResult.username ? ` as ${sfTestResult.username}` : ""}
                            {sfTestResult.instanceUrl ? ` @ ${sfTestResult.instanceUrl}` : ""}
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="font-medium">Connection failed</span>
                          <span className="block text-xs mt-0.5 opacity-80 break-all">
                            {sfTestResult.error}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {isAdmin && (
                  <Collapsible open={fieldCheckOpen} onOpenChange={setFieldCheckOpen}>
                    <CollapsibleTrigger asChild>
                      <Button variant="ghost" size="sm" className="flex items-center gap-2 text-muted-foreground hover:text-foreground -ml-1 h-8 px-2">
                        <ChevronDown className={`h-4 w-4 transition-transform ${fieldCheckOpen ? "rotate-180" : ""}`} />
                        Field Mapping Diagnostics
                        {sfFieldCheck && sfFieldCheck.fields.some(f => !f.resolved) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:border-amber-800">
                            {sfFieldCheck.fields.filter(f => !f.resolved).length} missing
                          </span>
                        )}
                        {sfFieldCheck && sfFieldCheck.fields.every(f => f.resolved) && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800">
                            All OK
                          </span>
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-2 rounded-lg border bg-muted/20 p-3 space-y-1.5">
                        {isLoadingFieldCheck ? (
                          <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Checking fields...
                          </div>
                        ) : sfFieldCheck?.error ? (
                          <div className="text-sm text-destructive">{sfFieldCheck.error}</div>
                        ) : sfFieldCheck ? (
                          <>
                            {sfFieldCheck.fields.map((field) => (
                              <div key={field.envVar} className="flex items-start gap-2 text-xs">
                                {field.resolved ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                                ) : (
                                  <XCircle className="h-3.5 w-3.5 text-amber-500 mt-0.5 shrink-0" />
                                )}
                                <div className="min-w-0">
                                  <span className="font-mono text-muted-foreground">{field.configuredField}</span>
                                  {field.isDefault && <span className="ml-1 text-muted-foreground/60">(default)</span>}
                                  {field.label && <span className="ml-1 text-foreground/70">· {field.label}</span>}
                                  {!field.resolved && (
                                    <div className="text-amber-600 dark:text-amber-500 mt-0.5">
                                      Not found on {field.sobject}. Use the Advanced section above to override the API field name.
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                            <div className="pt-1 border-t">
                              <button
                                onClick={() => refetchFieldCheck()}
                                className="text-xs text-muted-foreground hover:text-foreground underline-offset-2 hover:underline"
                              >
                                Re-run check
                              </button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="p-4 border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 rounded-lg flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-amber-800 dark:text-amber-400">Salesforce not connected</h4>
                    <p className="text-sm text-amber-700/80 dark:text-amber-500/80 mt-1">
                      Connect your Salesforce org to enable opportunity import, deal push, and the supplier directory.
                    </p>
                    {sfStatus?.error && !sfConnected && (
                      <p className="text-xs text-amber-700/70 dark:text-amber-500/70 mt-1 font-mono">
                        {sfStatus.error}
                      </p>
                    )}
                  </div>
                </div>
                <Button onClick={() => setShowConnectDialog(true)}>
                  <Plug className="mr-2 h-4 w-4" />
                  Connect Salesforce
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" /> Products
              </CardTitle>
              <CardDescription>
                Manage the product catalog used when creating deals.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingCatalog ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : (
                <>
                  {(!catalogProducts || catalogProducts.length === 0) && (
                    <p className="text-sm text-muted-foreground">
                      No products in the catalog yet. Add your first product below.
                    </p>
                  )}
                  {(catalogProducts ?? []).map((product) => (
                    <div key={product.id} className="flex items-center gap-2 p-2 rounded-lg border bg-muted/20">
                      {editingProductId === product.id ? (
                        <>
                          <Input
                            ref={editInputRef}
                            className="h-8 flex-1"
                            value={editingProductName}
                            onChange={(e) => setEditingProductName(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSaveEdit(product.id);
                              if (e.key === "Escape") { setEditingProductId(null); setEditingProductName(""); }
                            }}
                          />
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-primary"
                            onClick={() => handleSaveEdit(product.id)}
                            disabled={updateProduct.isPending}
                          >
                            {updateProduct.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8"
                            onClick={() => { setEditingProductId(null); setEditingProductName(""); }}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <span className="flex-1 text-sm font-medium">{product.name}</span>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-muted-foreground"
                            onClick={() => handleStartEdit(product.id, product.name)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-muted-foreground hover:text-destructive"
                            onClick={() => handleDeleteProduct(product.id, product.name)}
                            disabled={deleteProduct.isPending}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  ))}
                </>
              )}
              <div className="flex gap-2 pt-2 border-t">
                <Input
                  placeholder="New product name…"
                  value={newProductName}
                  onChange={(e) => setNewProductName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleAddProduct(); }}
                  className="flex-1"
                />
                <Button
                  onClick={handleAddProduct}
                  disabled={!newProductName.trim() || createProduct.isPending}
                >
                  {createProduct.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                  Add
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setBulkDialogOpen(true)}
                >
                  <List className="mr-2 h-4 w-4" />
                  Bulk add
                </Button>
              </div>

              <BulkAddProductsDialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen} />
            </CardContent>
          </Card>
        )}

        {me?.isAdmin && (
          <Card id="mcp">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Plug className="h-5 w-5" /> MCP Servers
              </CardTitle>
              <CardDescription>
                Configure the built-in TDM MCP server and register external MCP servers for AI integrations.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <McpServersSection />
            </CardContent>
          </Card>
        )}

        {me?.isAdmin && (
          <SystemHealthCard isAdmin={isAdmin} />
        )}

        {me?.isAdmin && (
          <DatabaseHealthCard isAdmin={isAdmin} />
        )}

        {me?.isAdmin && (
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Clock className="h-5 w-5" /> Background Jobs
                </CardTitle>
                <CardDescription className="mt-1">
                  Monitor outbound email sends and Salesforce pushes. Retry or cancel failed jobs.
                </CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={loadJobData} disabled={jobsLoading}>
                {jobsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                <span className="ml-2">Refresh</span>
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {jobStats && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                  {(["queued","running","done","failed","cancelled"] as const).map(s => (
                    <button
                      key={s}
                      onClick={() => setJobsStatus(s)}
                      className={`rounded-lg border p-3 text-center transition-colors cursor-pointer ${jobsStatus === s ? "border-primary bg-primary/5 ring-1 ring-primary" : "hover:border-muted-foreground/40"}`}
                    >
                      <div className={`text-2xl font-bold ${s === "failed" ? "text-destructive" : s === "done" ? "text-green-600" : s === "running" ? "text-blue-600" : "text-muted-foreground"}`}>
                        {jobStats[s]}
                      </div>
                      <div className="text-xs text-muted-foreground capitalize mt-0.5">{s}</div>
                    </button>
                  ))}
                </div>
              )}

              <div className="space-y-2">
                {jobsLoading && jobs.length === 0 && (
                  <div className="flex items-center justify-center py-8 text-muted-foreground text-sm">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading jobs…
                  </div>
                )}
                {!jobsLoading && jobs.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    No {jobsStatus} jobs.
                  </p>
                )}
                {jobs.map(job => (
                  <div key={job.id} className="rounded-lg border p-3 space-y-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-mono bg-muted rounded px-1.5 py-0.5">{job.kind}</span>
                          <span className={`text-xs font-medium ${job.status === "failed" ? "text-destructive" : job.status === "done" ? "text-green-600" : job.status === "running" ? "text-blue-600" : "text-muted-foreground"}`}>
                            {job.status}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            attempt {job.attempts}/{job.maxAttempts}
                          </span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Created {formatDateTime(job.createdAt)}
                          {job.completedAt && <> · Completed {formatDateTime(job.completedAt)}</>}
                        </div>
                        {job.lastError && (
                          <div className="text-xs text-destructive mt-1 break-words">
                            {job.lastError}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {(job.status === "failed" || job.status === "cancelled") && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleJobRetry(job.id)}
                            disabled={jobActionPending === job.id}
                          >
                            {jobActionPending === job.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCcw className="h-3 w-3" />}
                            <span className="ml-1">Retry</span>
                          </Button>
                        )}
                        {(job.status === "queued" || job.status === "failed") && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => handleJobCancel(job.id)}
                            disabled={jobActionPending === job.id}
                          >
                            <XCircle className="h-3 w-3" />
                            <span className="ml-1">Cancel</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {me?.isAdmin && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-5 w-5" /> Administration
              </CardTitle>
              <CardDescription>
                Admin-only tools and actions.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 border rounded-lg bg-muted/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <h4 className="font-medium">Export Registrations</h4>
                  <p className="text-sm text-muted-foreground mt-1">
                    Download a CSV export of all deal registrations across the organization.
                  </p>
                </div>
                <Button onClick={handleExportCsv} disabled={isExporting}>
                  {isExporting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                  Download CSV
                </Button>
              </div>

              <div className="p-4 border rounded-lg bg-muted/20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <RotateCcw className="h-5 w-5 mt-0.5 text-muted-foreground" />
                  <div>
                    <h4 className="font-medium">Reset Demo Data</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Wipe all demo records (deals, registrations, supplier contacts, support tickets) and re-run the seed script. Useful when onboarding new team members or after a live demo. Real (non-demo) records are not touched.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="text-destructive hover:bg-destructive/10 hover:text-destructive border-destructive/30"
                  onClick={() => setShowResetDemoDialog(true)}
                  disabled={resetDemoData.isPending}
                  data-testid="button-reset-demo-data"
                >
                  {resetDemoData.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="mr-2 h-4 w-4" />
                  )}
                  Reset demo data
                </Button>
              </div>

              <AdminUsersSection currentUserId={me?.id ?? null} />

              <div className="p-4 border rounded-lg bg-muted/20 space-y-3">
                <div className="flex items-start gap-3">
                  <LifeBuoy className="h-5 w-5 mt-0.5 text-muted-foreground" />
                  <div className="flex-1">
                    <h4 className="font-medium">Support Email</h4>
                    <p className="text-sm text-muted-foreground mt-1">
                      Email address that receives notifications when a user submits a new support ticket.
                      Leave blank to fall back to the <code className="font-mono text-xs">SUPPORT_EMAIL_TO</code> environment variable.
                    </p>
                  </div>
                </div>
                {isLoadingAdminSettings ? (
                  <Skeleton className="h-10 w-full" />
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="support-email">Notification email</Label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Input
                          id="support-email"
                          type="email"
                          placeholder={adminSettings?.supportEmailFromEnv ?? "support@example.com"}
                          value={supportEmailInput}
                          onChange={(e) => setSupportEmailInput(e.target.value)}
                          data-testid="input-support-email"
                        />
                        <Button
                          onClick={handleSaveSupportEmail}
                          disabled={
                            updateAdminSettings.isPending ||
                            supportEmailInput.trim() === (adminSettings?.supportEmail ?? "")
                          }
                          data-testid="button-save-support-email"
                        >
                          {updateAdminSettings.isPending && (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          )}
                          Save
                        </Button>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {adminSettings?.supportEmailEffective ? (
                        <>
                          Currently sending to{" "}
                          <span className="font-medium text-foreground">
                            {adminSettings.supportEmailEffective}
                          </span>
                          {!adminSettings.supportEmail && adminSettings.supportEmailFromEnv && (
                            <> (from environment variable)</>
                          )}
                          .
                        </>
                      ) : (
                        <>No support email configured. Notifications will not be sent.</>
                      )}
                    </div>
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <ConnectSalesforceDialog
        open={showConnectDialog}
        onOpenChange={setShowConnectDialog}
      />

      <Dialog open={showResetDemoDialog} onOpenChange={setShowResetDemoDialog}>
        <DialogContent data-testid="dialog-reset-demo-data">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-destructive" />
              Reset demo data?
            </DialogTitle>
            <DialogDescription className="pt-2">
              This will permanently delete every record whose Salesforce ID starts with
              <code className="font-mono text-xs mx-1 rounded bg-muted px-1 py-0.5">DEMO-</code>
              (and support tickets prefixed with <code className="font-mono text-xs mx-1 rounded bg-muted px-1 py-0.5">[Demo]</code>),
              including their registrations, channel managers and history. The seed script will then run again to re-create a fresh demo dataset.
              <br /><br />
              Your real (non-demo) deals, suppliers and tickets are not affected. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowResetDemoDialog(false)}
              disabled={resetDemoData.isPending}
              data-testid="button-cancel-reset-demo"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleResetDemoData}
              disabled={resetDemoData.isPending}
              data-testid="button-confirm-reset-demo"
            >
              {resetDemoData.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-2 h-4 w-4" />
              )}
              {resetDemoData.isPending ? "Resetting…" : "Yes, wipe and re-seed"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
